import React, {Component} from 'react';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import PropTypes from 'prop-types';
import {withAuth0} from '@auth0/auth0-react';

import config from '../../../config/main';
import MediaPlayerPricingTabs from '../../common/pricingTabs/MediaPlayerPricingTabs';
import PricingTabs from '../../common/pricingTabs/PricingTabs';
import inject from '../../hoc/injectHoc';
import {onboardingRoute, userPaymentRoute} from '../../routePaths';
import {PAYMENT_PLAN_LIFETIME} from '../../../constants/subscriptionPlanConstants';

import './pricingPage.scss';

/**
 * Check if the user has a plan
 *
 * @param {{}} user
 * @returns {boolean}
 */
const userHasCompanyPlan = (user) => {
  if (!user || !user.projectContentCompany) {
    return false;
  }

  return user.projectContentCompany.planId || user.projectContentCompany.requiresPlanUpdate;
};

/**
 * The PricingPage component.
 */
export class PricingPage extends Component {
  /**
   * Triggered when the component is first mounted to the page.
   */
  componentDidMount() {
    const {apiUserGetMeStore} = this.props;

    apiUserGetMeStore.refresh();
  }

  /**
   * Navigates the user to a defined route
   *
   * @param {string} toRoute
   * @param {{}} options
   */
  goToRoute = (toRoute, options = {}) => {
    const {apiUserGetMeStore, routerStore} = this.props;

    apiUserGetMeStore.getPromise().then(() => {
      routerStore.push(toRoute, options);
    }).catch(() => {
      const {loginWithRedirect} = this.props.auth0;

      try {
        loginWithRedirect({
          authorizationParams: {
            'screen_hint': 'signup',
            'redirect_uri': `${config.app.url}/signup${toRoute.search ? `?${toRoute.search}` : ''}`,
          }});
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('ERROR SIGNING UP: ', err);
      }
    });
  }

  /**
   * Triggered when the user chooses a plan.
   *
   * @param {{}} plan The plan object from the database.
   */
  onPlanSelect = (plan) => {
    const {apiUserGetMeStore, showMediaPlayerPricing} = this.props;

    const user = apiUserGetMeStore.getFulfilled();
    const userHasPlan = userHasCompanyPlan(user);

    const queryParams = new URLSearchParams({
      planId: plan.id,
    });

    if (showMediaPlayerPricing) {
      queryParams.append('interior', 'true');
    }

    this.goToRoute({
      pathname: userHasPlan ? userPaymentRoute : onboardingRoute,
      search: queryParams.toString(),
    });
  };

  /**
   * Triggered when the user purchases a la carte credits.
   *
   * @param {{
   *  download: number,
   *  request: number
   * }} alaCarteCredits
   */
  onPurchaseCredits = (alaCarteCredits) => {
    const {apiUserGetMeStore, showMediaPlayerPricing} = this.props;

    const queryParams = new URLSearchParams({
      downloadCreditQty: alaCarteCredits.download,
      requestCreditQty: alaCarteCredits.request
    });

    if (showMediaPlayerPricing) {
      queryParams.append('interior', 'true');
    }

    const user = apiUserGetMeStore.getFulfilled();
    const userHasPlan = userHasCompanyPlan(user);

    this.goToRoute({
      pathname: userHasPlan ? userPaymentRoute : onboardingRoute,
      search: queryParams.toString()
    });
  }

  /**
   * Triggered when the user purchases a media player.
   *
   * @param {'lifetime' | 'monthly'} mediaPlayerPlan
   * @param {number} quantity
   */
  onPurchaseMediaPlayer = (mediaPlayerPlan, quantity) => {
    const {apiUserGetMeStore} = this.props;

    const key = mediaPlayerPlan === PAYMENT_PLAN_LIFETIME
      ? 'lifetimeMediaPlayerQty'
      : 'monthlyMediaPlayerQty';

    const queryParams = new URLSearchParams({
      [key]: quantity,
      interior: 'true',
    });

    const user = apiUserGetMeStore.getFulfilled();
    const userHasPlan = userHasCompanyPlan(user);

    this.goToRoute({
      pathname: userHasPlan ? userPaymentRoute : onboardingRoute,
      search: queryParams.toString()
    });
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {showMediaPlayerPricing} = this.props;
    const user = this.props.apiUserGetMeStore.getFulfilled();

    return (
      <div id="pricing-page" className="system-page full-height container-fluid">
        {showMediaPlayerPricing && (<div className="container">
          <MediaPlayerPricingTabs
            onPlanSelect={this.onPlanSelect}
            onPurchase={this.onPurchaseMediaPlayer}
            user={user}
          />
        </div>)}
        <div className="container px-0">
          <PricingTabs
            onPlanSelect={this.onPlanSelect}
            onPurchaseCredits={this.onPurchaseCredits}
            user={user}
          />
        </div>
      </div>
    );
  }
}

PricingPage.propTypes = {
  apiUserGetMeStore: MobxPropTypes.observableObject,
  auth0: PropTypes.object,
  routerStore: MobxPropTypes.observableObject,

  showMediaPlayerPricing: PropTypes.bool,
};

PricingPage.defaultProps = {
  showMediaPlayerPricing: false,
};

PricingPage.wrappedComponent = {};
PricingPage.wrappedComponent.propTypes = {
  apiUserGetMeStore: MobxPropTypes.observableObject.isRequired,
  auth0: MobxPropTypes.observableObject.isRequired,
  routerStore: MobxPropTypes.observableObject.isRequired,
};

export default withAuth0(inject(PricingPage)(
  observer(PricingPage)
));
